import {priority} from '@priority-software/priority-proto-api/lib';
import {numFieldsPerTab} from '../consts';
import {DesignPanels} from '../enums';
import {FormViewEnum} from '../../../stores/Form/Form';

export const isTabDesign = (activePanel: DesignPanels, view: FormViewEnum): boolean => {
    return activePanel === DesignPanels.FIELDS && view === FormViewEnum.form;
};

export const isInvalidDesign = (data: priority.netitems.form.RowViewTabsDesign[]) => {
    const _data = serializeTabData(data);
    for (let i = 0; i < _data.length; i++) {
        const invalidDesign = _data[i].ColumnsDesign.find((c, index) =>
            isInvalidTabDesign(_data[i].ColumnsDesign, index, i)
        );
        if (invalidDesign) {
            return true;
        }
    }
    return false;
};

const findSplit = visibleData => {
    // if first Newarae is set on numFieldsPerTab index or later,
    // the split is numFieldsPerTab
    let firstSplit = visibleData.findIndex((i, index) => i.Newarea && index !== 0);
    if (firstSplit === -1 || firstSplit >= numFieldsPerTab) {
        return numFieldsPerTab;
    }
    // if not, then we need to check if the fields in the part till the next Newarea
    // could fit into the same column
    else {
        let splitsCount = 1;
        for (let i = firstSplit + 1; i < visibleData.length; i++) {
            // if numFieldsPerTab reached, no need to iterate further
            if (i + splitsCount > numFieldsPerTab) {
                break;
            }
            if (!visibleData[i].Newarea) {
                continue;
            }
            if (i + splitsCount <= numFieldsPerTab) {
                firstSplit = i;
                splitsCount++;
            }
        }
        return firstSplit;
    }
};

export const isInvalidTabDesign = (
    data: priority.netitems.form.IRowViewColumnsDesign[],
    index: number,
    parentTabIndex: number
) => {
    const item: priority.netitems.form.IRowViewColumnsDesign = data[index];
    if (item.Hidden) {
        return false;
    }
    if (parentTabIndex === 0) {
        // in main area it's enough to check if the number of fields (plus newareas that are counted as fields)
        // do not exceed numFieldsPerTab limit
        const designIndex: number = getDesignIndex(data, index);
        return designIndex > numFieldsPerTab;
    } else {
        const visibleData: priority.netitems.form.IRowViewColumnsDesign[] = data.filter(
            i => !i.Hidden
        );
        const visibleIndex = visibleData.findIndex(i => i.Name === item.Name);
        // first split is set by reaching numFieldsPerTab limit
        const firstSplit = findSplit(visibleData);
        // everything that fits into the first column is ok
        if (visibleIndex < firstSplit) {
            return false;
        } else {
            // the second column will be ok if all the columns (with newAreas) will fit into one column
            const dataAfterSplit = visibleData.filter((i, index) => index >= firstSplit);
            const indexAfterSplit = dataAfterSplit.findIndex(i => i.Name === item.Name);
            const secondColumnDesignIndex: number = getDesignIndex(dataAfterSplit, indexAfterSplit);
            return secondColumnDesignIndex > numFieldsPerTab;
        }
    }
};

export const getDesignIndex = (
    data: priority.netitems.form.IRowViewColumnsDesign[],
    itemPosition: number
) => {
    let designIndex = 0;
    for (let i = 0; i <= itemPosition; i++) {
        const item = data[i];
        if (!item.Hidden) {
            if (item.Newarea && i !== 0 && designIndex !== numFieldsPerTab) {
                designIndex = designIndex + 2;
            } else {
                designIndex++;
            }
        }
    }
    return designIndex;
};

export const serializeTabData = data => {
    const serializedData = data.map(t => {
        const tab = {...t};
        const tabItems = [];
        const items = tab.ColumnsDesign;
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.Merge) {
                item.merged = items[i + 1];
                tabItems.push(item);
                i++;
            } else {
                tabItems.push(item);
            }
        }
        tab.ColumnsDesign = tabItems;
        return tab;
    });
    return serializedData;
};

export const deserializeTabData = data => {
    const deserializedRes = data.map(tab => {
        const tabClone = {...tab};
        tabClone.ColumnsDesign = tabClone.ColumnsDesign.flatMap(i => {
            if (i.merged) {
                const merged = i.merged;
                delete i.merged;
                return [i, merged];
            } else {
                return i;
            }
        });
        return tabClone;
    });
    return deserializedRes;
};
